/**
 * TODA TAREA NACE POR LA PUERTA (SALV-PUERTA-API-T1, D-91 = a; orden (b) de Ares #24167).
 *
 * DE DONDE SALE. Con nexo-api PR 459 (a890708), POST /api/v1/Tasks deja de insertar directo: pasa por
 * trabajo_preparar + trabajo_crear_tarea. El cuerpo suma decision, porQue, clave y missionCode, y lo que la
 * puerta no deja pasar vuelve como 409 ProblemDetails:
 *   { type: "https://nexoapi.com/problems/puerta_x", title: "La tarea no pasa la puerta",
 *     detail: "PUERTA_X: <por que>", status: 409 }
 * ApiBase lo deja como { ok:false, code:409, error: detail, details: <el cuerpo> }.
 *
 * LAS REGLAS QUE LA BASE APLICA (trabajo_guardas_v2 y trabajo_crear_tarea), y que el panel dice ANTES del viaje:
 *   - decision: desde el panel siempre 'nueva' (los parecidos los calcula la puerta adentro: el panel no los ve)
 *   - por que: obligatorio con 'nueva' (por que no sirve ninguno de los parecidos)
 *   - clave: la familia en MAYUSCULAS sin el -Tn (^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$); el codigo sale CLAVE-Tn
 *   - mision: la tarea va en una mision VIVA (no completed, cancelled ni archived)
 * Y las que solo sabe la base (se dicen por su causa cuando vuelven):
 *   - PUERTA_SIN_OBJETIVO: sin [OBJETIVO-DIA] publicado hoy solo entra trabajo critico
 *   - PUERTA_TOPE: la cuarta tarea nueva del dia lleva en el por que "SI de Ares #id" (la sesion de Josse esta exenta)
 *
 * Compatible hacia atras: el API de hoy (sin el 459) ignora los campos nuevos y sigue creando.
 * Sin DOM: lo prueba scripts/check-puerta-tareas.mjs con la forma EXACTA del 459.
 */

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** La misma regla de la base para la clave. */
const FORMA_CLAVE = /^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/;
const CON_TN = /-T[0-9]+$/;

/** Estados de mision que la puerta NO acepta. */
export const MISION_CERRADA = ['completed', 'cancelled', 'archived'];

/**
 * Una clave propuesta desde el titulo: sin tildes, en mayusculas, las tres primeras palabras con guion y sin -Tn.
 * "Revisar el inventario de EC" -> "REVISAR-INVENTARIO-EC". Es solo una sugerencia: la persona la cambia.
 */
export function claveSugerida(titulo) {
  const vacias = new Set(['EL', 'LA', 'LOS', 'LAS', 'DE', 'DEL', 'EN', 'Y', 'A', 'AL', 'POR', 'PARA', 'CON', 'UN', 'UNA', 'QUE', 'SE']);
  const palabras = texto(titulo)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .split(' ')
    .filter((p) => p && !vacias.has(p));
  let clave = palabras.slice(0, 3).join('-').replace(CON_TN, '');
  while (clave && !/^[A-Z]/.test(clave)) clave = clave.replace(/^[^A-Z]+-?/, '');
  return clave;
}

/** La clave como la escribe la persona, en la forma de la base (mayusculas, guiones, sin -Tn). */
export function normalizarClave(escrita) {
  return texto(escrita)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/\s+/g, '-')
    .replace(/[^A-Z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function claveValida(clave) {
  const c = texto(clave);
  return FORMA_CLAVE.test(c) && !CON_TN.test(c);
}

/**
 * Los campos de la puerta para el cuerpo de POST /api/v1/Tasks, o lo que falta (con el campo a senalar).
 * @param {{clave: string, porQue: string, missionCode: string}} f
 * @returns {{ok: true, campos: {decision: 'nueva', porQue: string, clave: string, missionCode: string}} | {ok: false, campo: string, error: string}}
 */
export function camposPuerta(f) {
  const missionCode = texto(f?.missionCode);
  if (!missionCode) return { ok: false, campo: 'mision', error: 'Elige la mision donde va la tarea (la puerta solo deja crear dentro de una mision viva)' };
  const clave = normalizarClave(f?.clave);
  if (!clave) return { ok: false, campo: 'clave', error: 'Escribe la clave de la familia (por ejemplo DAN-AREA); la tarea sale DAN-AREA-T1, -T2...' };
  if (!claveValida(clave)) {
    return { ok: false, campo: 'clave', error: CON_TN.test(clave)
      ? `La clave va sin el -Tn: escribe ${clave.replace(CON_TN, '')} y la puerta le pone el numero`
      : 'La clave empieza con letra y solo lleva letras, numeros y guiones (por ejemplo DAN-AREA)' };
  }
  const porQue = texto(f?.porQue);
  if (!porQue) return { ok: false, campo: 'porque', error: 'Escribe por que es una tarea nueva (por que no sirve ninguna de las que ya existen)' };
  return { ok: true, campos: { decision: 'nueva', porQue, clave, missionCode } };
}

/** El codigo PUERTA_X de una respuesta 409 (del detail o del type), o '' si no es de la puerta. */
export function codigoPuerta(res) {
  if (Number(res?.code) !== 409) return '';
  const det = res?.details || {};
  const desdeTexto = /PUERTA_[A-Z_]+/.exec(texto(det.detail) || texto(res?.error));
  if (desdeTexto) return desdeTexto[0];
  const desdeTipo = /problems\/(puerta_[a-z_]+)$/.exec(texto(det.type));
  return desdeTipo ? desdeTipo[1].toUpperCase() : '';
}

/** Que decir cuando la puerta no dejo pasar la tarea, en idioma de persona y por su causa. */
export function motivoPuerta(res) {
  const codigo = codigoPuerta(res);
  const detalle = texto(res?.details?.detail) || texto(res?.error);
  switch (codigo) {
    case 'PUERTA_MISION': return 'La mision elegida ya cerro o no existe: la tarea va en una mision viva. Elige otra.';
    case 'PUERTA_SIN_OBJETIVO': return 'Hoy todavia no esta publicado el objetivo del dia: hasta que Ares lo publique solo entra trabajo critico.';
    case 'PUERTA_TOPE': return 'Esta persona ya tiene 3 tareas nuevas hoy: la cuarta lleva en el por que "SI de Ares #numero".';
    case 'PUERTA_CLAVE': return 'La clave no tiene la forma de la puerta: MAYUSCULAS, con guiones y sin el -Tn (por ejemplo DAN-AREA).';
    case 'PUERTA_POR_QUE': return 'Falta el por que: una tarea nueva dice por que no sirve ninguna de las que ya existen.';
    case 'PUERTA_DECISION': return 'La puerta no recibio la decision: recarga el panel (puede ser una version vieja en cache).';
    case 'PUERTA_TOKEN': return 'La puerta se demoro y su turno vencio: vuelve a intentar.';
    // nexo-api 461 (SALV-DEPENDENCIAS-T1): depende_de invalida al crear; el detalle de la base dice cual y por que.
    case 'PUERTA_DEPENDENCIA': return 'Una dependencia no sirve: ' + (detalle.replace(/^PUERTA_DEPENDENCIA:\s*/, '') || 'depende_de lleva codigos de tarea vivos');
    case '': return detalle || 'No se pudo crear la tarea';
    default: return detalle || `La puerta no dejo pasar la tarea (${codigo})`;
  }
}

/** Las misiones vivas (las que la puerta acepta), ordenadas por codigo, de una o varias respuestas de la API. */
export function misionesVivas(...respuestas) {
  const vistas = new Map();
  for (const res of respuestas) {
    const d = res?.data?.data ?? res?.data ?? {};
    const items = Array.isArray(d) ? d : (d.items ?? d.Items ?? []);
    for (const m of Array.isArray(items) ? items : []) {
      const code = texto(m?.code ?? m?.Code);
      const status = texto(m?.status ?? m?.Status).toLowerCase();
      if (!code || MISION_CERRADA.includes(status)) continue;
      vistas.set(code, { code, title: texto(m?.title ?? m?.Title), status });
    }
  }
  return [...vistas.values()].sort((a, b) => a.code.localeCompare(b.code));
}
